import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ShoppingBasket } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useBasket } from "@/context/BasketContext";
import { useSession } from "@/context/SessionContext";
import { LANGUAGE_IDS, LANG_FLAGS, LANG_LABELS } from "@/i18n/translations";
import { withMarketPath } from "@/lib/marketRoutes";
import { requestHomeSection } from "@/lib/homeSections";

// Menupunkter er scroll-ankre til sektioner på forsiden — undtagen "Mængde pr. dæk",
// der åbner beregnings-siden (/hvor-meget-skal-mit-daek) via sin "to".
// Teksterne kommer fra den globale sprogpakke (t("nav.<id>")).
const menu = [
  { id: "forside" },
  { id: "saadan" },
  { id: "paafyldning" },
  { id: "maengde", to: "/hvor-meget-skal-mit-daek" },
  { id: "emballager" },
  { id: "brochure" },
  { id: "faq" },
  { id: "kontakt" },
];

const linkCls = (active) =>
  `whitespace-nowrap font-display text-xs xl:text-sm font-semibold uppercase tracking-widest transition-colors [text-shadow:_0_1px_8px_rgb(0_0_0_/_85%)] ${
    active ? "text-brand-blue" : "text-slate-200 hover:text-brand-blue"
  }`;

const mobileLinkCls = (active) =>
  `text-left font-display text-base font-semibold uppercase tracking-widest transition-colors py-2.5 border-b border-brand-border/50 last:border-0 ${
    active ? "text-brand-blue" : "text-slate-200 hover:text-brand-blue"
  }`;

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { count } = useBasket();
  const { languageId, setLanguageId, t } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("forside");
  const marketPath = (path) => withMarketPath(location.pathname, path);
  const localPath = location.pathname.replace(/^\/(dk|se|gb)(?=\/|$)/i, "") || "/";
  const isHomepage = localPath === "/";
  const logoSrc = isHomepage ? "/assets/logo-homepage.webp" : "/assets/logo-banner.png?v=4";

  // Scroll-spy på forsiden: marker den sektion, der er i view, som aktiv i menuen
  useEffect(() => {
    if (localPath !== "/") return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveSection(e.target.id)),
      { rootMargin: "-35% 0px -60% 0px" }
    );
    menu.filter((m) => !m.to).forEach((m) => {
      const el = document.getElementById(m.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [localPath]);

  // Punkter med "to" er aktive på deres egen side; ankre er aktive når sektionen er i view
  const isActive = (m) =>
    m.to ? localPath === m.to : localPath === "/" && activeSection === m.id;

  const goToSection = (id) => {
    setMenuOpen(false);
    requestHomeSection(id);
    if (localPath !== "/") {
      navigate(marketPath(`/#${id}`));
    } else {
      scrollToId(id, { settle: true });
    }
  };

  // Punkter med "to" åbner en side; øvrige scroller til deres sektion på forsiden
  const go = (m) => {
    if (m.to) {
      setMenuOpen(false);
      navigate(marketPath(m.to));
      return;
    }
    goToSection(m.id);
  };

  const preloadSection = (m) => {
    if (!m.to) requestHomeSection(m.id, "preload");
  };

  return (
    // Én samlet sticky header: banner-logoet (flus mod venstre kant, m. egen baggrund)
    // + telefon + kurv på række 1, menuen på række 2, tynd streg under.
    // Mobil: menurækken er skjult — menuen åbnes via hamburger-knap øverst til højre.
    <header data-testid="nav-sticky" className="sticky top-0 z-[60]">
      <div className="flex items-center gap-3">
        <Link to={marketPath("/")} data-testid="nav-logo" aria-label="InvictusA — til forsiden" className="block min-w-0">
          <img
            src={logoSrc}
            alt={t("nav.logoAlt")}
            width={isHomepage ? "886" : "756"}
            height={isHomepage ? "193" : "165"}
            data-testid="nav-logo-image"
            fetchPriority={isHomepage ? "high" : undefined}
            className="h-12 sm:h-16 lg:h-20 xl:h-24 w-auto max-w-[56vw] sm:max-w-[420px] block -translate-x-[2%] object-contain object-left-top"
          />
        </Link>
        <div className="flex-1" />
        {/* Mobil: højre side deles i to etager — flag øverst, kurv + menu nedenunder,
            så hamburger-knappen ikke skubbes ud af billedet af det brede banner-logo.
            Fra sm og opefter ligger alt på én række som før. */}
        <div className="flex flex-col items-end gap-2 pr-4 sm:flex-row sm:items-center sm:gap-5 sm:pr-6">
          <a
            href="tel:+4543449800" data-testid="nav-phone"
            className="hidden sm:flex items-center gap-2 font-mono-tech text-xs text-slate-200 hover:text-brand-blue transition-colors [text-shadow:_0_1px_8px_rgb(0_0_0_/_85%)]"
          >
            <Phone size={14} /> +45 43 44 98 00
          </a>
          {/* Sprogflag: alle klarlagte sprog kan vælges frit (også sprog uden aktivt land).
              Auto-valget ved landing bruger kun landets AcceptedLanguages.
              shrink-0: flagene må aldrig klemmes væk af logoet på små skærme */}
          <div data-testid="lang-flags" className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {LANGUAGE_IDS.map((l) => (
              <button
                key={l}
                data-testid={`lang-flag-${l}`}
                onClick={() => setLanguageId(l)}
                aria-label={LANG_LABELS[l]}
                title={LANG_LABELS[l]}
                className={`transition-all cursor-pointer rounded-[2px] ${
                  languageId === l ? "ring-2 ring-brand-blue" : "opacity-55 hover:opacity-100"
                }`}
              >
                <img
                  src={`https://flagcdn.com/w40/${LANG_FLAGS[l]}.png`}
                  alt={LANG_LABELS[l]}
                  className="w-6 h-[17px] object-cover rounded-[2px] block"
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-5">
            <Link
              to={marketPath("/kurv")} data-testid="nav-basket" aria-label={t("nav.basket")}
              onClick={() => setMenuOpen(false)}
              className="relative shrink-0 text-slate-100 hover:text-brand-blue transition-colors drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]"
            >
              <ShoppingBasket size={22} />
              {count > 0 && (
                <span
                  data-testid="nav-basket-count"
                  className="absolute -top-2 -right-2 bg-brand-blue text-[#0B0E12] font-mono-tech text-[10px] font-bold min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              data-testid="nav-menu-toggle"
              aria-label={menuOpen ? t("nav.menuClose") : t("nav.menuOpen")}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden shrink-0 text-slate-100 hover:text-brand-blue transition-colors drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      {menuOpen && (
        <nav
          data-testid="nav-mobile-menu"
          className="lg:hidden absolute top-full inset-x-0 bg-[#0B0E12] border-b border-brand-border flex flex-col px-6 py-3"
        >
          {menu.map((m) => (
            <button
              key={m.id}
              data-testid={`nav-mobile-${m.id}`}
              onClick={() => go(m)}
              onFocus={() => preloadSection(m)}
              className={mobileLinkCls(isActive(m))}
            >
              {t(`nav.${m.id}`)}
            </button>
          ))}
        </nav>
      )}
      <nav data-testid="nav-menu" className="hidden lg:flex max-w-7xl mx-auto px-4 sm:px-6 items-center gap-6 overflow-x-auto h-11">
        {menu.map((m) => (
          <button
            key={m.id}
            data-testid={`nav-${m.id}`}
            onClick={() => go(m)}
            onPointerEnter={() => preloadSection(m)}
            onFocus={() => preloadSection(m)}
            className={linkCls(isActive(m))}
          >
            {t(`nav.${m.id}`)}
          </button>
        ))}
      </nav>
    </header>
  );
}
